import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import type { UserRole } from '@/types/auth';
import { PageSkeleton } from '@/components/ui';
import { MFA_CONFIG } from '@/lib/utils';

interface AuthGuardProps {
  children: ReactNode;
  /**
   * Required roles to access this route.
   * If not specified, only authentication is required.
   */
  requiredRoles?: UserRole[];
}

/**
 * Route protection component — vereinfacht fuer SRP Direct Auth.
 * Kein Redirect zu Hosted UI mehr, nur Navigation zu /login.
 * Spec 0045: MFA-Enforcement fuer Plattform-Rollen.
 *
 * @see Spec 0043 Section 6.1 — AuthGuard Redirect aendern
 * @see Spec 0045 Section 4.2 — AuthGuard MFA-Check
 */
export function AuthGuard({ children, requiredRoles }: AuthGuardProps) {
  const { isAuthenticated, isLoading, hasRole, mfaSetupComplete, user } = useAuth();
  const location = useLocation();

  // Auth-State wird noch geladen
  if (isLoading) {
    return <PageSkeleton />;
  }

  // Nicht authentifiziert → Login-Seite
  if (!isAuthenticated) {
    sessionStorage.setItem('redirectAfterLogin', location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }

  // Spec 0045: MFA-Check fuer Plattform-Rollen
  if (user && MFA_CONFIG.requiredRoles.includes(user.role) && !mfaSetupComplete) {
    // Nur redirecten wenn nicht schon auf /mfa-setup
    if (location.pathname !== '/mfa-setup') {
      return <Navigate to="/mfa-setup" replace />;
    }
  }

  // Rollen-Check
  if (requiredRoles && requiredRoles.length > 0) {
    if (!hasRole(requiredRoles)) {
      return <Navigate to="/?error=forbidden" replace />;
    }
  }

  return <>{children}</>;
}
